import {
    VuexModule,
    Module,
    getModule,
    Mutation,
    Action,
} from 'vuex-module-decorators';
import store from '@/store';
import { login, getConnectedUser } from '@/api/api';
import { Role } from '@/models/Role';
import jwt_decode from 'jwt-decode';
import Subscription from '@/models/Subscription';
import UserToken from '@/models/UserToken';

interface JWT {
    iat?: number;
    exp?: number;
}

function isTokenValid(token: string): boolean {
    if (token && token.trim && token.trim()) {
        try {
            const tokenDecoded: JWT = jwt_decode(token);
            const now = Math.floor(new Date().getTime() / 1000);

            if (tokenDecoded.exp) {
                if (tokenDecoded.exp < now) {
                    console.warn('Token expired');
                    return false;
                }
            }
            return true;
        } catch (error) {
            console.warn(error);
            return false;
        }
    }
    return false;
}

interface AuthState {
    token: string;
    logged: boolean;
    isKochise: boolean;
    roles: Role[];
    userID: number;
    email: string;
    firstname: string;
    isRestricted: Boolean;
    lastname: string;
    logo: string;
    isTrialActive: boolean | null;
    isSubscriptionActive: boolean | null;
    enabledAt: Date | null;
    subscriptions: Subscription[];
    personal_color: string;
    userTokens: UserToken[];
    crm_url: string;
}

interface LoginArg {
    username: string;
    password: string;
}

@Module({ dynamic: true, store, name: 'auth', namespaced: true })
class AuthModule extends VuexModule implements AuthState {
    public token = '';
    public logged = false;
    public roles: Role[] = [];
    public userID: number = -1;
    public email: string = '';
    public firstname: string = '';
    public isRestricted: boolean = false;
    public lastname: string = '';
    public logo: string = '';
    public isTrialActive: boolean | null = null;
    public isSubscriptionActive: boolean | null = null;
    public enabledAt: Date | null = null;
    public subscriptions: Subscription[] = [];
    public isKochise: boolean = false;
    public personal_color: string = '';
    public userTokens: UserToken[] = [];
    public crm_url: string = '';

    public initialized = false;

    @Mutation
    setInitialized() {
        this.initialized = true;
    }

    @Mutation
    setCrmUrl(url: string): void {
        this.crm_url = url;
    }

    @Mutation
    setLogged(logged: boolean): void {
        this.logged = logged;
    }

    @Mutation
    setToken(token: string): void {
        this.token = token;
    }

    @Mutation
    setUserID(userID: number) {
        this.userID = userID;
    }

    @Mutation
    setRoles(roles: any) {
        this.roles = roles;
    }
    @Mutation
    setEmail(email: string) {
        this.email = email;
    }
    @Mutation
    setLastname(lastname: string) {
        this.lastname = lastname;
    }
    @Mutation
    setFirstname(firstname: string) {
        this.firstname = firstname;
    }
    @Mutation
    setIsRestricted(isRestricted: boolean) {
        this.isRestricted = isRestricted;
    }
    @Mutation
    setLogo(logo: string) {
        this.logo = logo;
    }
    @Mutation
    setTrialActive(trialActive: boolean | null) {
        this.isTrialActive = trialActive;
    }
    @Mutation
    setSubscriptionActive(subscriptionActive: boolean | null) {
        this.isSubscriptionActive = subscriptionActive;
    }
    @Mutation
    setEnabledAt(enabledAt: Date) {
        this.enabledAt = enabledAt;
    }
    @Mutation
    setIsKochise(isKochise: boolean) {
        this.isKochise = isKochise;
    }
    @Mutation
    setSubscriptions(subscriptions: Subscription[]) {
        this.subscriptions = subscriptions;
    }

    @Mutation
    setPersonalColor(personal_color: string) {
        this.personal_color = personal_color;
    }

    @Mutation
    setUserTokens(userTokens: UserToken[]) {
        this.userTokens = userTokens;
    }

    get isSubscribed() {
        return (
            this.isTrialActive !== false ||
            this.isSubscriptionActive !== false ||
            this.roles.includes('ROLE_ADMIN')
        );
    }

    get crmUrl() {
        return this.crm_url
    }

    @Action({ rawError: true })
    async login({ username, password }: LoginArg): Promise<void> {
        try {
            const resp = await login(username, password);
            const data = resp.data;

            this.context.commit('setToken', data.token);
            this.context.commit('setLogged', true);
            await this.getUser();
            location.reload();
        } catch (error) {
            throw error;
        }
    }

    @Action
    async loginWithToken(token: string): Promise<void> {
        try {
            this.context.commit('setToken', token);
            this.context.commit('setLogged', true);
            await this.getUser();
            location.reload();
        } catch (error) {
            throw error;
        }
    }

    @Action
    async logout(): Promise<void> {
        this.context.commit('setLogged', false);
        this.context.commit('setToken', '');
        this.context.commit('setUserID', -1);
        this.context.commit('setRoles', []);
        this.context.commit('setEmail', '');
        this.context.commit('setFirstname', '');
        this.context.commit('setIsRestricted', false);

        this.context.commit('setLastname', '');
        this.context.commit('setLogo', '');
        this.context.commit('setTrialActive', null);
        this.context.commit('setSubscriptionActive', null);
        this.context.commit('setEnabledAt', null);
        this.context.commit('setSubscriptions', []);
        this.context.commit('personal_color', null);
        this.context.commit('userTokens', [])
        this.context.commit('setCrmUrl', '')

        this.saveToLocalstorage();
        localStorage.removeItem('service')
        location.reload();
    }

    @Action
    async init(): Promise<void> {
        if (this.initialized) {
            return;
        }
        this.context.commit('setInitialized');

        this.restoreFromLocalstorage();
        if (this.logged) {
            await this.getUser();
        }
    }

    @Action
    async getUser() {
        try {
            const resp = await getConnectedUser();
            const user = resp.data;
            const userID = user.id;
            const email = user.email;
            const firstname = user.firstname;
            const isRestricted = user.is_restricted;
            const lastname = user.lastname;
            const logo = user.logo_image;
            const roles = user.roles;
            const isKochise = user.is_kochise;
            const personal_color = user.personal_color;
            const userTokens = user.user_tokens;
            const crm_url = user.crm_url;
            this.context.commit('setIsKochise', isKochise);
            this.context.commit('setUserID', userID);
            this.context.commit('setRoles', roles);
            this.context.commit('setEmail', email);
            this.context.commit('setFirstname', firstname);
            this.context.commit('setIsRestricted', isRestricted);
            this.context.commit('setCrmUrl', crm_url);

            this.context.commit('setLastname', lastname);
            this.context.commit('setLogo', logo);
            this.context.commit(
                'setTrialActive',
                user.is_trial_active !== undefined ? user.is_trial_active : null
            );
            this.context.commit(
                'setSubscriptionActive',
                user.is_subscription_active !== undefined
                    ? user.is_subscription_active
                    : null
            );
            this.context.commit(
                'setEnabledAt',
                user.enabled_at ? new Date(user.enabled_at) : null
            );
            this.context.commit(
                'setSubscriptions',
                user.subscriptions ? user.subscriptions : []
            );
            this.context.commit('setPersonalColor', personal_color ? personal_color : '#891A2B');
            this.context.commit(
                'setUserTokens',
                user.user_tokens ? user.user_tokens : []
            );

            this.context.commit(
                'setCrmUrl',
                crm_url
            );

            this.saveToLocalstorage();
        } catch (error) {
            throw error;
        }
    }

    @Action
    saveToLocalstorage(): void {
        const state: AuthState = {
            isKochise: this.isKochise,
            logged: this.logged,
            token: this.token,
            roles: this.roles,
            userID: this.userID,
            firstname: this.firstname,
            isRestricted: this.isRestricted,
            lastname: this.lastname,
            logo: this.logo,
            email: this.email,
            isTrialActive: this.isTrialActive,
            isSubscriptionActive: this.isSubscriptionActive,
            enabledAt: this.enabledAt,
            subscriptions: this.subscriptions,
            personal_color: this.personal_color,
            userTokens: this.userTokens,
            crm_url: this.crm_url
        };
        localStorage.setItem('auth', JSON.stringify(state));
    }

    @Action
    restoreFromLocalstorage(): void {
        const stateStr = localStorage.getItem('auth');
        if (stateStr) {
            const state: AuthState = JSON.parse(stateStr);

            if (isTokenValid(state.token)) {
                this.context.commit('setIsKochise', state.isKochise);
                this.context.commit('setLogged', state.logged);
                this.context.commit('setToken', state.token);
                this.context.commit('setUserID', state.userID);
                this.context.commit('setRoles', state.roles);
                this.context.commit('setEmail', state.email);
                this.context.commit('setFirstname', state.firstname);
                this.context.commit('setIsRestricted', state.isRestricted);

                this.context.commit('setLastname', state.lastname);
                this.context.commit('setLogo', state.logo);
                this.context.commit('setTrialActive', state.isTrialActive);
                this.context.commit(
                    'setSubscriptionActive',
                    state.isSubscriptionActive
                );
                this.context.commit('setEnabledAt', state.enabledAt);
                this.context.commit('setSubscriptions', state.subscriptions);
                this.context.commit('setPersonalColor', state.personal_color);
                this.context.commit('setUserTokens', state.userTokens);
                this.context.commit('setCrmUrl', state.crm_url)
            } else {
                this.saveToLocalstorage();
            }
        }
    }
}

export default getModule(AuthModule);
